// client/src/pages/bank-statements/Transactions.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import useTransactions from '../../hooks/useTransactions';
import useCategories from '../../hooks/useCategories';
import PaginationControl from '../../components/PaginationControl';
import { orderCategoriesHierarchically, optionPrefix } from '../../utils/categoryTree';

const computeDateFilter = (preset, customFrom, customTo) => {
  const now = new Date();
  if (preset === 'last-month') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 1);
    return { from: d.toISOString() };
  }
  if (preset === 'last-3-months') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 3);
    return { from: d.toISOString() };
  }
  if (preset === 'custom') {
    return {
      from: customFrom ? new Date(customFrom).toISOString() : undefined,
      to: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : undefined
    };
  }
  return {};
};

// Slider à deux poignées (min + max) sur une seule barre.
function DualRange({ min, max, step, valueMin, valueMax, onChangeMin, onChangeMax }) {
  const span = max - min || 1;
  const pctMin = Math.max(0, Math.min(100, ((valueMin - min) / span) * 100));
  const pctMax = Math.max(0, Math.min(100, ((valueMax - min) / span) * 100));
  return (
    <div className="dual-range">
      <div className="dual-range__track" />
      <div
        className="dual-range__selected"
        style={{ left: `${pctMin}%`, width: `${Math.max(0, pctMax - pctMin)}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={valueMin}
        onChange={(e) => onChangeMin(Math.min(Number(e.target.value), valueMax))}
        aria-label="Montant minimum"
        style={{ zIndex: pctMin > 90 ? 5 : 3 }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={valueMax}
        onChange={(e) => onChangeMax(Math.max(Number(e.target.value), valueMin))}
        aria-label="Montant maximum"
        style={{ zIndex: 4 }}
      />
    </div>
  );
}

function Transactions() {
  const {
    accounts,
    transactions,
    paginationInfo,
    amountMax,
    loading,
    error,
    fetchAccounts,
    fetchTransactions,
    applyCategoryLocally
  } = useTransactions();
  const {
    categories,
    error: categoriesError,
    fetchCategories,
    setTransactionCategory
  } = useCategories();

  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  // Filtres
  const [filterCategory, setFilterCategory] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Fourchette de montant (valeur absolue). min/maxVal = position des curseurs,
  // amountFilter = valeurs appliquées (débouncées) envoyées au serveur.
  const [minVal, setMinVal] = useState(0);
  const [maxVal, setMaxVal] = useState(0);
  const [amountFilter, setAmountFilter] = useState({});
  // Sélection de lignes : Map id -> montant (permet la somme même entre pages).
  const [selected, setSelected] = useState(new Map());

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await fetchAccounts();
        if (data.length > 0) setSelectedAccountId(data[0].id);
      } catch (err) {
        // Erreur gérée dans le hook
      }
    };
    loadAccounts();
    fetchCategories().catch(() => {});
  }, [fetchAccounts, fetchCategories]);

  const dateFilter = useMemo(
    () => computeDateFilter(datePreset, customFrom, customTo),
    [datePreset, customFrom, customTo]
  );

  // Debounce de la recherche : on n'interroge le serveur qu'après une pause de frappe.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Réinitialise la fourchette de montant quand on change de compte.
  useEffect(() => {
    setMinVal(0);
    setMaxVal(0);
  }, [selectedAccountId]);

  // Cale le curseur haut sur la borne des données dès qu'elle est connue.
  useEffect(() => {
    if (amountMax > 0 && maxVal === 0) setMaxVal(amountMax);
  }, [amountMax, maxVal]);

  // Debounce de la fourchette : on n'interroge le serveur qu'après une pause de glissement.
  useEffect(() => {
    const id = setTimeout(() => {
      setAmountFilter({
        minAmount: minVal > 0 ? minVal : undefined,
        maxAmount: maxVal > 0 && maxVal < amountMax ? maxVal : undefined
      });
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [minVal, maxVal, amountMax]);

  const amountStep = Math.max(1, Math.round((amountMax || 100) / 200));

  const reload = useCallback(() => {
    if (!selectedAccountId) return;
    fetchTransactions(selectedAccountId, currentPage, 50, {
      categoryId: filterCategory || undefined,
      search: search || undefined,
      ...dateFilter,
      ...amountFilter
    });
  }, [
    selectedAccountId,
    currentPage,
    filterCategory,
    search,
    dateFilter,
    amountFilter,
    fetchTransactions
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Changer un filtre remet à la première page (évite d'être bloqué sur une page vide).
  const onAccount = (v) => {
    setSelectedAccountId(v);
    setCurrentPage(1);
  };
  const onFilterCategory = (v) => {
    setFilterCategory(v);
    setCurrentPage(1);
  };
  const onDatePreset = (v) => {
    setDatePreset(v);
    setCurrentPage(1);
  };
  const onCustomFrom = (v) => {
    setCustomFrom(v);
    setCurrentPage(1);
  };
  const onCustomTo = (v) => {
    setCustomTo(v);
    setCurrentPage(1);
  };

  // On vide la sélection quand le jeu de données change (filtres/compte/recherche).
  useEffect(() => {
    setSelected(new Map());
  }, [selectedAccountId, filterCategory, dateFilter, search, amountFilter]);

  // Sélection façon explorateur : clic simple = 1 ligne, Ctrl/Cmd+clic = ajout/retrait.
  const handleRowSelect = (e, tx) => {
    const amount = Number(tx.amount);
    setSelected((prev) => {
      const next = new Map(prev);
      if (e.ctrlKey || e.metaKey) {
        if (next.has(tx.id)) next.delete(tx.id);
        else next.set(tx.id, amount);
      } else if (next.size === 1 && next.has(tx.id)) {
        next.clear();
      } else {
        next.clear();
        next.set(tx.id, amount);
      }
      return next;
    });
  };

  const selectedSum = useMemo(
    () => [...selected.values()].reduce((sum, a) => sum + a, 0),
    [selected]
  );

  // Reclassement optimiste : la ligne change immédiatement et la liste n'est PAS
  // rechargée (un refetch ferait clignoter tout le tableau). En cas d'échec on
  // remet la catégorie précédente, l'erreur étant affichée par le hook.
  const handleCategoryChange = async (tx, rawCategoryId) => {
    const categoryId = rawCategoryId || null;
    const previousCategoryId = tx.categoryId || null;
    if (categoryId === previousCategoryId) return;

    applyCategoryLocally([tx.id], categoryId);
    try {
      const result = await setTransactionCategory(tx.id, categoryId, applyToSimilar);
      // La propagation aux libellés similaires touche d'autres lignes : le serveur
      // renvoie les ids concernés, on les met à jour sur place.
      if (result && result.ids && result.ids.length) applyCategoryLocally(result.ids, categoryId);
    } catch (err) {
      applyCategoryLocally([tx.id], previousCategoryId);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const colorFor = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.color : 'transparent';
  };

  return (
    <div>
      <h1>Liste des Transactions</h1>

      {error && <Alert variant="danger">{error}</Alert>}
      {categoriesError && <Alert variant="danger">{categoriesError}</Alert>}

      <Card
        className="border shadow-sm mb-4"
        style={{ position: 'sticky', top: '0.5rem', zIndex: 1020 }}
      >
        <Card.Body>
          <Form.Control
            type="search"
            placeholder="Rechercher dans les libellés…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="mb-3"
          />
          <Row className="g-3 align-items-end">
            <Col md={6} lg={3}>
              <Form.Label htmlFor="filter-account" className="small text-muted mb-1">
                Compte
              </Form.Label>
              <Form.Select
                id="filter-account"
                value={selectedAccountId}
                onChange={(e) => onAccount(e.target.value)}
                disabled={loading.accounts}
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} lg={3}>
              <Form.Label htmlFor="filter-category" className="small text-muted mb-1">
                Catégorie
              </Form.Label>
              <Form.Select
                id="filter-category"
                value={filterCategory}
                onChange={(e) => onFilterCategory(e.target.value)}
              >
                <option value="">Toutes les catégories</option>
                <option value="none">Non catégorisées</option>
                {orderCategoriesHierarchically(categories).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {optionPrefix(cat.depth)}
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} lg={3}>
              <Form.Label htmlFor="filter-period" className="small text-muted mb-1">
                Période
              </Form.Label>
              <Form.Select
                id="filter-period"
                value={datePreset}
                onChange={(e) => onDatePreset(e.target.value)}
              >
                <option value="">Tout l'historique</option>
                <option value="last-month">Dernier mois</option>
                <option value="last-3-months">3 derniers mois</option>
                <option value="custom">Personnalisé…</option>
              </Form.Select>
            </Col>
            {datePreset === 'custom' && (
              <Col md={6} lg={3}>
                <Row className="g-2">
                  <Col xs={6}>
                    <Form.Label htmlFor="filter-date-from" className="small text-muted mb-1">
                      Du
                    </Form.Label>
                    <Form.Control
                      id="filter-date-from"
                      type="date"
                      value={customFrom}
                      onChange={(e) => onCustomFrom(e.target.value)}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Label htmlFor="filter-date-to" className="small text-muted mb-1">
                      Au
                    </Form.Label>
                    <Form.Control
                      id="filter-date-to"
                      type="date"
                      value={customTo}
                      onChange={(e) => onCustomTo(e.target.value)}
                    />
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
          <Row className="g-3 mt-0">
            <Col md={12} lg={6}>
              <div className="small text-muted mb-1 d-flex justify-content-between">
                <span>Montant (valeur absolue)</span>
                <span className="fw-semibold text-body">
                  {minVal.toFixed(0)} € –{' '}
                  {amountMax > 0 && maxVal >= amountMax
                    ? `${amountMax} €+`
                    : `${maxVal.toFixed(0)} €`}
                </span>
              </div>
              <DualRange
                min={0}
                max={amountMax || 100}
                step={amountStep}
                valueMin={minVal}
                valueMax={maxVal}
                onChangeMin={setMinVal}
                onChangeMax={setMaxVal}
              />
              <div className="d-flex justify-content-between align-items-center small text-muted">
                <span>0 €</span>
                {(minVal > 0 || (amountMax > 0 && maxVal < amountMax)) && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() => {
                      setMinVal(0);
                      setMaxVal(amountMax);
                    }}
                  >
                    Réinitialiser
                  </Button>
                )}
                <span>{amountMax || '—'} €</span>
              </div>
            </Col>
          </Row>
          <div className="mt-3">
            <Form.Check
              type="switch"
              id="applyToSimilar"
              label="Appliquer les changements de catégorie aux libellés similaires"
              checked={applyToSimilar}
              onChange={(e) => setApplyToSimilar(e.target.checked)}
            />
          </div>
          {selected.size > 0 && (
            <div className="mt-3 pt-3 border-top d-flex align-items-center gap-3 flex-wrap">
              <Badge bg="primary" pill>
                {selected.size} ligne{selected.size > 1 ? 's' : ''} sélectionnée
                {selected.size > 1 ? 's' : ''}
              </Badge>
              <span style={{ fontSize: 15 }}>
                Somme :{' '}
                <strong className={selectedSum >= 0 ? 'text-success' : 'text-danger'}>
                  {selectedSum.toFixed(2)} €
                </strong>
              </span>
              <Button size="sm" variant="outline-secondary" onClick={() => setSelected(new Map())}>
                Effacer la sélection
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {loading.transactions ? (
        <Spinner animation="border" />
      ) : transactions.length === 0 ? (
        <Alert variant="info">Aucune transaction ne correspond à ces filtres.</Alert>
      ) : (
        <>
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>
            Astuce : cliquez une ligne pour la sélectionner, Ctrl+clic (Cmd sur Mac) pour en ajouter
            ou retirer. La somme s&apos;affiche dans l&apos;encadré ci-dessus.
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th style={{ minWidth: '180px' }}>Catégorie</th>
                <th className="text-end">Montant</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr
                  key={tx.id}
                  onClick={(e) => handleRowSelect(e, tx)}
                  className={selected.has(tx.id) ? 'table-active' : undefined}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <td>{formatDate(tx.date)}</td>
                  <td>{tx.description}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="d-inline-block rounded-circle flex-shrink-0"
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorFor(tx.categoryId)
                        }}
                      />
                      <Form.Select
                        size="sm"
                        value={tx.categoryId || ''}
                        onChange={(e) => handleCategoryChange(tx, e.target.value)}
                      >
                        <option value="">— Non catégorisée —</option>
                        {orderCategoriesHierarchically(categories).map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {optionPrefix(cat.depth)}
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </td>
                  <td
                    className={`text-end ${Number(tx.amount) > 0 ? 'text-success' : 'text-danger'}`}
                  >
                    {Number(tx.amount).toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-center">
            <PaginationControl
              currentPage={currentPage}
              totalPages={paginationInfo.totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Transactions;
